// Google Identity Services sign-in button.
//
// Lazy-loads the GIS script on first render, renders the official button into
// the ref'd div (no React control over the button's internals — Google owns
// it), and forwards the returned ID token to AuthContext.signInWithGoogle.
//
// Hidden entirely when client_id is unset (via /api/config) so disabled envs
// don't show a dead button.

import { useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/AuthContext";
import { useConfig } from "../lib/ConfigContext";
import { ApiError } from "../lib/api";
import { useNavigate, useLocation } from "react-router-dom";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (opts: {
            client_id: string;
            callback: (resp: { credential: string }) => void;
            ux_mode?: "popup" | "redirect";
            auto_select?: boolean;
          }) => void;
          renderButton: (parent: HTMLElement, opts: Record<string, unknown>) => void;
          prompt: () => void;
        };
      };
    };
  }
}

let _gsiLoading: Promise<void> | null = null;
function loadGsi(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (_gsiLoading) return _gsiLoading;
  _gsiLoading = new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load Google Identity Services"));
    document.head.appendChild(s);
  });
  return _gsiLoading;
}

export function GoogleButton({
  label = "continue_with",
  onCredential,
  bare = false,
}: {
  label?: "signin_with" | "signup_with" | "continue_with";
  // When provided, the returned credential is handed here instead of the
  // default sign-in + redirect (used by the Account page to *link* an account).
  onCredential?: (credential: string) => Promise<void>;
  // Drop the "or" divider chrome (for embedding outside the auth forms).
  bare?: boolean;
}) {
  const cfg = useConfig();
  const { signInWithGoogle } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const mountRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!cfg?.google_client_id || !mountRef.current) return;
    const clientId = cfg.google_client_id;
    const parent = mountRef.current;
    let disposed = false;

    loadGsi()
      .then(() => {
        if (disposed || !window.google) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (resp: { credential: string }) => {
            try {
              if (onCredential) {
                await onCredential(resp.credential);
              } else {
                await signInWithGoogle(resp.credential);
                const next = (loc.state as { from?: string } | null)?.from ?? "/library";
                nav(next, { replace: true });
              }
            } catch (e) {
              setError(e instanceof ApiError ? e.detail : "Google sign-in failed.");
            }
          },
          ux_mode: "popup",
        });
        window.google.accounts.id.renderButton(parent, {
          type: "standard",
          theme: "filled_black",
          text: label,
          shape: "rectangular",
          width: 340,
        });
      })
      .catch((e) => setError(e.message ?? "Could not load Google Sign-In."));

    return () => { disposed = true; };
  }, [cfg?.google_client_id, label, signInWithGoogle, onCredential, nav, loc.state]);

  if (!cfg) return null;                       // still loading /api/config
  if (!cfg.google_client_id) return null;      // feature disabled in this env

  return (
    <div className={bare ? "" : "mt-6"}>
      {bare ? null : (
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-smallcaps text-ink-500 mb-4">
          <span className="flex-1 h-px bg-ink-700" />
          <span>or</span>
          <span className="flex-1 h-px bg-ink-700" />
        </div>
      )}
      <div ref={mountRef} className={bare ? "flex justify-start" : "flex justify-center"} />
      {error ? <p className={`mt-3 text-xs text-oxblood${bare ? "" : " text-center"}`}>{error}</p> : null}
    </div>
  );
}
